import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Check, Code, Flag, Package, type LucideIcon } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import { cn } from '@/lib/utils'
import {
  AX_PRODUCT_TYPES,
  AX_PRODUCTS_QUERY_KEY,
  AxProductsApi,
  type AxProduct,
  type AxProductType,
} from '@/services/earnings/axProductsApi'
import { useNetwork } from '../contexts/NetworkContext'
import { SearchInput } from './SearchInput'
import { WizardSteps } from './WizardSteps'

const TYPE_ICONS: Record<AxProductType, LucideIcon> = {
  STANDARD: Package,
  CREATIVE_TAG: Code,
  POLITICAL: Flag,
}

/** Group key for products that carry no label. */
const UNLABELLED = ''

/**
 * Products grouped under each of their labels, labels alphabetically and the
 * unlabelled ones last. A product with several labels shows in each group.
 */
function groupByLabel(products: AxProduct[]): [string, AxProduct[]][] {
  const groups = new Map<string, AxProduct[]>()
  for (const product of products) {
    for (const label of product.labels.length ? product.labels : [UNLABELLED]) {
      groups.set(label, [...(groups.get(label) ?? []), product])
    }
  }
  return [...groups].sort(([a], [b]) =>
    a === UNLABELLED ? 1 : b === UNLABELLED ? -1 : a.localeCompare(b)
  )
}

export interface ProductPickerWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Product id currently booked, or '' for none. */
  value: string
  /** Called with the picked product id, or '' for "no product". */
  onSelect: (productId: string) => void
}

/**
 * Picks a marketplace product in two steps: first what kind of product
 * (standard, creative tag only, political), then the product itself, grouped
 * by its labels.
 */
export function ProductPickerWizard({ open, onOpenChange, value, onSelect }: ProductPickerWizardProps) {
  const { t } = useTranslation()
  const { currentNetwork } = useNetwork()

  const { data: products = [], isLoading } = useQuery({
    queryKey: AX_PRODUCTS_QUERY_KEY,
    queryFn: () => AxProductsApi.getAll(),
    staleTime: 5 * 60 * 1000,
  })

  const current = products.find((p) => p.id === value)
  // Reopening on a booked product goes straight to its list, one Back away
  // from switching type.
  const [type, setType] = useState<AxProductType | null>(current?.type ?? null)
  const [search, setSearch] = useState('')

  // Start over on every opening — the parent opens the dialog, so this cannot
  // hang off `onOpenChange`.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setType(current?.type ?? null)
      setSearch('')
    }
  }

  function pick(productId: string) {
    onSelect(productId)
    onOpenChange(false)
  }

  const countByType = useMemo(() => {
    const counts: Partial<Record<AxProductType, number>> = {}
    for (const p of products) counts[p.type] = (counts[p.type] ?? 0) + 1
    return counts
  }, [products])

  // Political products only belong on a political network — unless there are
  // some anyway, which should then still be reachable.
  const types = AX_PRODUCT_TYPES.filter(
    (type) => type !== 'POLITICAL' || currentNetwork?.politicalApp || countByType.POLITICAL
  )

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase()
    const ofType = products.filter(
      (p) =>
        p.type === type &&
        (!term ||
          p.name.toLowerCase().includes(term) ||
          p.labels.some((l) => l.toLowerCase().includes(term)))
    )
    return groupByLabel(ofType)
  }, [products, type, search])

  const step = type ? 1 : 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('productWizard.title')}</DialogTitle>
          <DialogDescription>
            {step === 0
              ? t('productWizard.typeDescription')
              : t('productWizard.productDescription', { type: t(`products.type_${type}`) })}
          </DialogDescription>
          <WizardSteps
            className="pt-2"
            steps={[
              {
                title: t('productWizard.stepType'),
                summary: type ? t(`products.type_${type}`) : undefined,
              },
              { title: t('productWizard.stepProduct') },
            ]}
            current={step}
            onStepClick={(i) => i === 0 && setType(null)}
          />
        </DialogHeader>

        {step === 0 && (
          <ItemGroup className="gap-2">
            {types.map((option) => {
              const Icon = TYPE_ICONS[option]
              const count = countByType[option] ?? 0
              return (
                <Item key={option} asChild variant="outline">
                  <button
                    type="button"
                    disabled={!isLoading && !count}
                    onClick={() => setType(option)}
                    className="text-left hover:bg-accent/50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ItemMedia variant="icon">
                      <Icon />
                    </ItemMedia>
                    <ItemContent>
                      <ItemTitle>{t(`products.type_${option}`)}</ItemTitle>
                      <ItemDescription>{t(`productWizard.typeHelp_${option}`)}</ItemDescription>
                    </ItemContent>
                    <ItemActions>
                      <Badge variant="secondary">
                        {isLoading ? '…' : t('productWizard.productCount', { count })}
                      </Badge>
                    </ItemActions>
                  </button>
                </Item>
              )
            })}
          </ItemGroup>
        )}

        {step === 1 && (
          <div className="flex min-h-0 flex-col gap-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('lineItems.searchProducts')}
              delay={0}
            />
            <div className="flex max-h-96 flex-col gap-4 overflow-y-auto">
              {groups.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {t('lineItems.noProductsFound')}
                </p>
              )}
              {groups.map(([label, items]) => (
                <section key={label || 'unlabelled'} className="flex flex-col gap-1.5">
                  <h3 className="text-xs font-medium text-muted-foreground">
                    {label || t('productWizard.unlabelled')}
                  </h3>
                  <ItemGroup className="gap-1.5">
                    {items.map((product) => {
                      const selected = product.id === value
                      return (
                        <Item key={product.id} asChild size="sm" variant="outline">
                          <button
                            type="button"
                            onClick={() => pick(product.id)}
                            className={cn(
                              'text-left hover:bg-accent/50',
                              selected && 'border-primary'
                            )}
                          >
                            <ItemContent className="min-w-0">
                              <ItemTitle className="truncate">{product.name}</ItemTitle>
                              {product.description && (
                                <ItemDescription className="line-clamp-1">
                                  {product.description}
                                </ItemDescription>
                              )}
                            </ItemContent>
                            {selected && (
                              <ItemActions>
                                <Check className="size-4 text-(--ad-link)" />
                              </ItemActions>
                            )}
                          </button>
                        </Item>
                      )
                    })}
                  </ItemGroup>
                </section>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="sm:justify-between">
          <Button type="button" variant="ghost" onClick={() => pick('')}>
            {t('productWizard.noProduct')}
          </Button>
          {step === 1 && (
            <Button type="button" variant="outline" onClick={() => setType(null)}>
              <ArrowLeft className="size-4" />
              {t('productWizard.changeType')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
